import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import analyticsService from "../AnalyticsService";
import { useSelector } from "react-redux";

const pageNameMap = {
  "/register": "Registration Page",
  "/email-verified": "Post Registration Notification Page",
  "/activate": "Activate Page",
  "/login": "Login Page",
  "/forgot": "Forgot Password Page",
  "/financial-health-score": "Analyst Dashboard",
  "/ewiChatBot/ewi": "Insights Page",
  "/drilldown/graph": "Cluster Page",
  "/drilldown/list/": "View Details Page",
  "/bank/health-analysis/report": "Dashboard",
  "/bank/health-analysis/report/": "Dashboard",
  "/bank/health-analysis/report/financial-health-score": "Financial Health Score",
  "/cohort-management": "Cohort Management",
  "/history": "History",
  "/bank/campaign-management/": "Campaign Management",
  "/bank/campaign-management/email-templates": "Email Templates",
  "/bank/campaign-management/email-campaigns": "Email Campaigns",
  "/bank/campaign-management/consumers-list": "Consumers List",
  "/user-management": "User Management",
  "/pages/faq": "Support",
};

const resolvePageName = (currentPath) => {
  if (!currentPath) {
    return null;
  }

  const normalizedPath = currentPath.split("?")[0];

  if (normalizedPath.includes("/bank/health-analysis/report/")) {
    const reportSubPath = normalizedPath.replace("/bank/health-analysis/report", "");

    if (reportSubPath.includes("/financial-health-score")) {
      return "Financial Health Score";
    }

    if (reportSubPath.includes("/drilldown/graph/")) {
      return "Cluster Page";
    }

    if (reportSubPath.includes("/drilldown/list/")) {
      return "View Details Page";
    }

    return "Dashboard";
  }

  if (normalizedPath.includes("/bank/campaign-management/")) {
    return "Campaign Management";
  }

  const match = Object.entries(pageNameMap)
    .filter(([route]) =>
      normalizedPath === route || normalizedPath.startsWith(`${route}/`),
    )
    .sort((left, right) => right[0].length - left[0].length)[0];

  return match?.[1] || null;
};

export const usePageTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastTrackedContextKey = useRef(null);
  const currentDashboard = useSelector(
    (state) => state?.dashboardReducer?.currentDashboard,
  );
  const selectedCard = useSelector(
    (state) => state?.dashboardReducer?.selectedCard,
  );

  const currentPath = `${location?.pathname}${location?.search || ""}`;
  const currentDashboardTitle =
    currentDashboard?.CONFIG?.pageHeader?.pageTitle || null;
  const selectedCardName =
    currentDashboard?.KEY === 2
      ? selectedCard?.commercial_title
      : selectedCard?.retail_title;
  const trackingContextKey = `${currentPath}|${navigationType}|${currentDashboardTitle ?? ""}|${selectedCardName ?? ""}|${location?.pathname ?? ""}`;

  useEffect(() => {
    if (lastTrackedContextKey.current === trackingContextKey) {
      return undefined;
    }

    let timeoutId = null;
    let loadHandler = null;

    const runTracking = () => {
      lastTrackedContextKey.current = trackingContextKey;

      const pageName = resolvePageName(currentPath);

      analyticsService.trackPageView({
        page_name: pageName || document?.title,
        page_path: currentPath,
        navigation_type: navigationType,
        dashboard: currentDashboardTitle,
        navigated_from_card: selectedCardName || null,
      });
    };

    const scheduleTracking = () => {
      if (document.readyState === "complete") {
        timeoutId = window.setTimeout(runTracking, 150);
        return;
      }

      loadHandler = () => {
        timeoutId = window.setTimeout(runTracking, 150);
      };

      window.addEventListener("load", loadHandler, { once: true });
    };

    scheduleTracking();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (loadHandler) {
        window.removeEventListener("load", loadHandler);
      }
    };
  }, [
    trackingContextKey,
    currentPath,
    navigationType,
    currentDashboardTitle,
    selectedCardName,
  ]);
};
